'use client'

import { useEffect, useState } from 'react'
import { getFileUrl } from '@/lib/utils/file-url'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getMedicalReport,
    deleteMedicalReport,
    MedicalReport,
    MEDICAL_REPORT_TYPE_LABELS,
    MEDICAL_REPORT_TYPE_COLORS,
} from '@/lib/api/medical-reports/medical-report-api'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdArrowBack,
    MdArticle,
    MdEdit,
    MdDelete,
    MdPerson,
    MdCalendarToday,
    MdMedicalServices,
    MdAttachFile,
    MdPrint,
    MdVerified,
    MdDraw,
    MdDownload,
    MdOpenInNew,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import { SignDocumentModal } from '@/components/signature/SignDocumentModal'

export default function ReportDetailPage() {
    const navigate = useNavigate()
    const { canUpdate, canDelete } = useResourcePermissions('bookings', 'reports')
    const { id } = useParams<{ id: string }>()
    const [report, setReport] = useState<MedicalReport | null>(null)
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showSignModal, setShowSignModal] = useState(false)

    useEffect(() => {
        if (id) {
            loadReport()
        }
    }, [id])

    const loadReport = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getMedicalReport(parseInt(id, 10))
            if (response.success && response.result) {
                setReport(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar laudo')
                navigate('/bookings/reports')
            }
        } catch (error) {
            toast.error('Erro ao carregar laudo')
            navigate('/bookings/reports')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = () => {
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!id || !report) return

        setDeleting(true)
        try {
            const response = await deleteMedicalReport(parseInt(id, 10))
            if (response.success) {
                toast.success('Laudo excluído com sucesso!')
                navigate('/bookings/reports')
            } else {
                toast.error(response.error || 'Erro ao excluir laudo')
            }
        } catch (error) {
            toast.error('Erro ao excluir laudo')
        } finally {
            setDeleting(false)
            setShowDeleteModal(false)
        }
    }

    const handleSign = () => {
        setShowSignModal(true)
    }

    const handlePrint = () => {
        window.print()
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando detalhes do laudo...
                </p>
            </div>
        )
    }

    if (!report) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdArticle className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Laudo não encontrado
                    </h3>
                    <p className="text-sm text-base-content/60">
                        O laudo que você está procurando não existe
                    </p>
                </div>
                <button
                    onClick={() => navigate('/bookings/reports')}
                    className="btn btn-secondary"
                >
                    Voltar para Laudos
                </button>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center justify-between print:hidden">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/bookings/reports')}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 hidden md:flex lg:flex rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                            <MdArticle className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                {report.title}
                            </h1>
                            <p className="text-sm text-base-content/60">
                                Detalhes do laudo
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handlePrint}
                        className="btn btn-ghost gap-2"
                    >
                        <MdPrint className="text-lg" />
                    </button>
                    {canUpdate && !report.signedAt && (
                        <button
                            onClick={handleSign}
                            className="btn btn-success gap-2"
                        >
                            <MdDraw className="text-lg" />
                            <span className="hidden sm:inline">Assinar Digitalmente</span>
                        </button>
                    )}
                    {report.signedPdfUrl && (
                        <a
                            href={getFileUrl(report.signedPdfUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-ghost gap-2"
                            title="Baixar PDF assinado"
                        >
                            <MdDownload className="text-lg" />
                        </a>
                    )}
                    {canUpdate && (
                        <button
                            onClick={() =>
                                navigate(`/bookings/reports/edit/${report.id}`)
                            }
                            className="btn btn-secondary gap-2"
                        >
                            <MdEdit className="text-lg" />
                        </button>
                    )}
                    {canDelete && (
                        <button
                            onClick={handleDelete}
                            disabled={deleting}
                            className="btn btn-error btn-outline gap-2"
                        >
                            {deleting ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                <MdDelete className="text-lg" />
                            )}
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <span
                                className={`badge ${MEDICAL_REPORT_TYPE_COLORS[report.type]
                                    }`}
                            >
                                {MEDICAL_REPORT_TYPE_LABELS[report.type]}
                            </span>
                            {report.signedAt && (
                                <span className="badge badge-success gap-1">
                                    <MdVerified className="text-sm" />
                                    Assinado
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-base-content/70">
                            <MdCalendarToday />
                            <span>
                                {format(
                                    new Date(report.reportDate),
                                    "dd 'de' MMMM 'de' yyyy",
                                    { locale: ptBR }
                                )}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {report.client && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdPerson className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Paciente
                                    </p>
                                    <p className="font-semibold">
                                        {report.client.name}
                                    </p>
                                </div>
                            </div>
                        )}

                        {report.professional && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdMedicalServices className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Profissional
                                    </p>
                                    <p className="font-semibold">
                                        {report.professional.name}
                                    </p>
                                    {report.professional.registrationNumber && (
                                        <p className="text-sm text-base-content/60">
                                            {report.professional.councilName}{' '}
                                            {
                                                report.professional
                                                    .registrationNumber
                                            }
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {report.summary && (
                        <div className="mb-6">
                            <h3 className="text-lg font-bold text-base-content mb-3">
                                Resumo
                            </h3>
                            <div className="p-4 bg-base-200/50 rounded-xl">
                                <p className="text-base-content whitespace-pre-wrap">
                                    {report.summary}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="mb-6">
                        <h3 className="text-lg font-bold text-base-content mb-3">
                            Conteúdo
                        </h3>
                        <div className="p-4 bg-base-200/50 rounded-xl">
                            <div
                                className="text-base-content prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: report.content }}
                            />
                        </div>
                    </div>

                    {report.conclusion && (
                        <div className="mb-6">
                            <h3 className="text-lg font-bold text-base-content mb-3">
                                Conclusão
                            </h3>
                            <div className="p-4 bg-success/10 rounded-xl border border-success/20">
                                <p className="text-base-content whitespace-pre-wrap">
                                    {report.conclusion}
                                </p>
                            </div>
                        </div>
                    )}

                    {report.signedAt && (
                        <div className="mt-6 pt-6 border-t border-base-300/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <MdVerified className="text-2xl text-success" />
                                    <div>
                                        <p className="font-semibold text-success">
                                            Documento Assinado Digitalmente
                                        </p>
                                        <p className="text-sm text-base-content/60">
                                            Assinado por {report.signedBy} em{' '}
                                            {format(
                                                new Date(report.signedAt),
                                                "dd/MM/yyyy 'às' HH:mm",
                                                { locale: ptBR }
                                            )}
                                        </p>
                                    </div>
                                </div>
                                {report.signatureData && (report.signatureData as any).signatureLogId && (
                                    <a
                                        href={`/verify/${(report.signatureData as any).signatureLogId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-ghost btn-sm gap-1"
                                    >
                                        <MdOpenInNew className="text-sm" />
                                        Verificar
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {report.attachments && report.attachments.length > 0 && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden print:hidden">
                    <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                        <div className="flex items-center gap-2">
                            <MdAttachFile className="text-xl text-secondary" />
                            <h3 className="text-lg font-bold text-base-content">
                                Anexos
                            </h3>
                            <span className="badge badge-secondary badge-sm">
                                {report.attachments.length}
                            </span>
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="flex flex-col gap-3">
                            {report.attachments.map((attachment, index) => (
                                <div
                                    key={index}
                                    className="bg-base-200/50 rounded-xl border border-base-300/50 p-4"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <MdAttachFile className="text-lg text-secondary" />
                                                <a
                                                    href={attachment.filePath}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-semibold text-secondary hover:underline"
                                                >
                                                    {attachment.fileName}
                                                </a>
                                            </div>
                                            <div className="space-y-1 text-sm">
                                                <p>
                                                    <span className="font-medium">
                                                        Usuário:
                                                    </span>{' '}
                                                    {attachment.userName}
                                                </p>
                                                <p>
                                                    <span className="font-medium">
                                                        Descrição:
                                                    </span>{' '}
                                                    {attachment.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Excluir laudo"
                message="Tem certeza que deseja excluir este laudo?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />

            <SignDocumentModal
                isOpen={showSignModal}
                onClose={() => setShowSignModal(false)}
                documentType="MEDICAL_REPORT"
                documentId={report.id}
                documentTitle={report.title}
                onSigned={() => loadReport()}
            />
        </div>
    )
}
